import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '../patterns';
import { ChemicalStockBarsRow } from './ChemicalStockBarsRow';
import { enrichChemicalStockUsage, type EnrichedChemicalStockUsageRow } from '../../utils/chemicalStockUsageMath';
import type { ChemicalStockUsageRow } from '../../services/sprayDashboardService';
import './ChemicalStockUsageWidget.css';

type Filter  = 'all' | 'atrisk' | 'variance';
type Density = 'comfortable' | 'compact';

export interface ChemicalStockUsageWidgetProps {
  rows:            ChemicalStockUsageRow[];
  lowStockPct?:    number;
  varianceTolPct?: number;
  initialFilter?:  Filter;
  initialDensity?: Density;
}

const MOBILE_BREAKPOINT = '(max-width: 767px)';

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const matchesFilter = (r: EnrichedChemicalStockUsageRow, f: Filter): boolean => {
  if (f === 'all') return true;
  if (f === 'atrisk') return r.stockStatus !== 'sufficient';
  if (f === 'variance') return r.varianceStatus !== 'ontarget' && r.cPlanned > 0;
  return true;
};

export const ChemicalStockUsageWidget: React.FC<ChemicalStockUsageWidgetProps> = ({
  rows,
  lowStockPct,
  varianceTolPct,
  initialFilter = 'all',
  initialDensity = 'comfortable',
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filter, setFilter]   = useState<Filter>(initialFilter);
  const [density, setDensity] = useState<Density>(initialDensity);

  const enrichedRows = useMemo(
    () => enrichChemicalStockUsage(rows, { lowPct: lowStockPct, tolPct: varianceTolPct }),
    [rows, lowStockPct, varianceTolPct],
  );

  const filteredRows = useMemo(
    () => enrichedRows.filter(r => matchesFilter(r, filter)),
    [enrichedRows, filter],
  );

  const counts = useMemo(() => ({
    all:      enrichedRows.length,
    atrisk:   enrichedRows.filter(r => matchesFilter(r, 'atrisk')).length,
    variance: enrichedRows.filter(r => matchesFilter(r, 'variance')).length,
  }), [enrichedRows]);

  if (rows.length === 0) {
    return (
      <section
        className="csu-widget csu-widget--empty"
        data-testid="chemical-stock-usage-widget"
      >
        <Banner
          tone="offline"
          title={t('spray.dashboard.chemicalStockUsage.empty.title')}
          detail={t('spray.dashboard.chemicalStockUsage.empty.detail')}
        />
      </section>
    );
  }

  const bodyClassName = ['csu-rows', `density-${density}`, isMobile ? 'csu-is-mobile' : null]
    .filter(Boolean).join(' ');

  return (
    <section
      className={`csu-widget${isMobile ? ' csu-is-mobile' : ''}`}
      aria-labelledby="csu-title"
      data-testid="chemical-stock-usage-widget"
    >
      <header className="csu-chrome">
        <div className="csu-title-block">
          <div className="csu-eyebrow" aria-hidden="true">
            {t('spray.dashboard.chemicalStockUsage.eyebrow')}
          </div>
          <h2 id="csu-title" className="csu-title">
            {t('spray.dashboard.chemicalStockUsage.title')}
          </h2>
          <p className="csu-subtitle">
            {t('spray.dashboard.chemicalStockUsage.subtitle')}
          </p>
        </div>

        <div className="csu-chrome-controls">
          <div
            className="csu-chips"
            role="radiogroup"
            aria-label={t('spray.dashboard.chemicalStockUsage.aria.filters')}
          >
            {([
              { id: 'all',      label: t('spray.dashboard.chemicalStockUsage.filters.all'),      tone: '' },
              { id: 'atrisk',   label: t('spray.dashboard.chemicalStockUsage.filters.atrisk'),   tone: 'danger' },
              { id: 'variance', label: t('spray.dashboard.chemicalStockUsage.filters.variance'), tone: 'warning' },
            ] as const).map(c => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={filter === c.id}
                className={`csu-chip ${filter === c.id ? 'is-on' : ''} ${c.tone}`}
                onClick={() => setFilter(c.id)}
              >
                {c.label}
                <span className="csu-chip-count">{counts[c.id]}</span>
              </button>
            ))}
          </div>

          <div className="csu-chrome-right">
            <span className="csu-count">
              {t('spray.dashboard.chemicalStockUsage.count', { filtered: filteredRows.length, total: enrichedRows.length })}
            </span>
            {!isMobile && (
              <div
                className="csu-density"
                role="radiogroup"
                aria-label={t('spray.dashboard.chemicalStockUsage.aria.density')}
              >
                {([
                  { id: 'comfortable', label: t('spray.dashboard.chemicalStockUsage.density.comfortable') },
                  { id: 'compact',     label: t('spray.dashboard.chemicalStockUsage.density.compact') },
                ] as const).map(d => (
                  <button
                    key={d.id}
                    type="button"
                    role="radio"
                    aria-checked={density === d.id}
                    className={`csu-dchip ${density === d.id ? 'is-on' : ''}`}
                    onClick={() => setDensity(d.id)}
                  >{d.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="csu-body">
        {filteredRows.length === 0 ? (
          <p className="csu-filter-empty" role="status">
            {t('spray.dashboard.chemicalStockUsage.empty.filteredOut')}
          </p>
        ) : (
          // list-style:none suppresses ul list semantics in Safari/VoiceOver; role="list" restores them
          // eslint-disable-next-line jsx-a11y/no-redundant-roles
          <ul role="list" className={bodyClassName}>
            {filteredRows.map(r => (
              <li
                key={r.chemicalId}
                aria-label={t('spray.dashboard.chemicalStockUsage.aria.row', {
                  product: r.productName,
                  stockStatus: r.stockStatus,
                  varianceStatus: r.varianceStatus,
                })}
              >
                <ChemicalStockBarsRow row={r} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ChemicalStockUsageWidget;
